import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { baseUrl } from "../../baseurl";

axios.defaults.baseURL = baseUrl;

const FrameManagement = () => {
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [currentFrame, setCurrentFrame] = useState(null);

  const [formData, setFormData] = useState({
    frame_time: "",
  });

  useEffect(() => {
    loadFrames();
  }, []);

  const loadFrames = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("vsat_token");

      const res = await axios.get("admin/frame", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setFrames(res.data.frames);
      }
    } catch (error) {
      toast.error("Failed to load frames");
    }

    setLoading(false);
  };

  

  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const openAddModal = () => {
    setFormData({ frame_time: "" });
    setIsAddModalOpen(true);
  };

  const openEditModal = (frame) => {
    setCurrentFrame(frame);
    setFormData({
      frame_time: frame.frame_time,
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (frame) => {
    setCurrentFrame(frame);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setCurrentFrame(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.frame_time < 1 || formData.frame_time > 60) {
      toast.error("Frame time must be between 1 and 60 seconds");
      return;
    }

    try {
      const token = localStorage.getItem("vsat_token");

      let res;

      if (isAddModalOpen) {
        res = await axios.post("admin/frame/save", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (isEditModalOpen) {
        res = await axios.put(
          `admin/frame/update/${currentFrame._id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      if (res.data.success) {
        toast.success("Frame saved successfully");
        loadFrames();
        closeModals();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("vsat_token");

      const res = await axios.delete(
        `admin/frame/delete/${currentFrame._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        toast.success("Frame deleted successfully");
        loadFrames();
        closeModals();
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <ToastContainer />

      {/* Header */}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Frame Management</h1>
          <p className="text-gray-600">Manage frame time (seconds)</p>
        </div>

        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Frame
        </button>
      </div>

      {/* Table */}

      <div className="bg-white rounded-lg shadow border">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs text-gray-500">
                Frame Time
              </th>

              <th className="px-6 py-3 text-left text-xs text-gray-500">
                Created
              </th>

              <th className="px-6 py-3 text-left text-xs text-gray-500">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {frames.map((frame) => (
              <tr key={frame._id} className="border-t">
                <td className="px-6 py-4">{frame.frame_time} sec</td>

                <td className="px-6 py-4">
                  {new Date(frame.createdAt).toLocaleDateString()}
                </td>

                <td className="px-6 py-4 flex space-x-2">
                  <button
                    onClick={() => openEditModal(frame)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => openDeleteModal(frame)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}

            {frames.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center py-6 text-gray-500">
                  No frames found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}

      {(isAddModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
          <div className="bg-white rounded-lg w-96 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {isAddModalOpen ? "Add Frame Time" : "Edit Frame Time"}
              </h3>

              <button onClick={closeModals}>
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <label className="text-sm font-medium">
                Frame Time (seconds)
              </label>

              <input
                type="number"
                min="1"
                max="60"
                value={formData.frame_time}
                onChange={(e) =>
                  handleFormChange("frame_time", e.target.value)
                }
                className="w-full border px-3 py-2 rounded mt-1"
                required
              />
              
              <p className="text-xs text-gray-500 mt-1">
                Allowed: 1 – 60 seconds
              </p>

              <div className="flex mt-4 space-x-3">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 border py-2 rounded"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
          <div className="bg-white rounded-lg w-80 p-6">
            <h3 className="text-lg font-semibold mb-3">
              Delete Frame Time
            </h3>

            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this frame time?
            </p>

            <div className="flex space-x-3">
              <button
                onClick={closeModals}
                className="flex-1 border py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 text-white py-2 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameManagement;